import * as React from 'react';
import { IWatrRequestWeeks } from '@nkr/models/index';
import { WageAndTravelReimbursementRequestStatus } from '@nkr/enums/index';
import {
    WEEKLY_CAP,
    calculateAdjustedBaseAmount,
    calculateAdjustedWeeklyReimbursement
} from '@nkr/views/donors/watr/components/common/wage-computation';
import { isSelfEmployed } from '@nkr/views/donors/watr/components/utils/employment-type.utils';

interface IProps {
    weeks: IWatrRequestWeeks[];
    pendingWeeks: number[];
    loading?: boolean;
    actualWageReimbursementAmount?: number;
    calculatedWeeklyPayByYear?: number;
    employmentType?: number | string | null;
    incomePercentCovered?: number;
}

const money = (n: number) =>
    `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getStatusDisplay = (status: WageAndTravelReimbursementRequestStatus): { label: string; background: string; color: string } => {
    switch (status) {
        case WageAndTravelReimbursementRequestStatus.PendingApproval:
            return { label: 'Pending approval', background: '#fff3cd', color: '#856404' };
        case WageAndTravelReimbursementRequestStatus.PendingFix:
            return { label: 'Needs fix', background: '#f8d7da', color: '#721c24' };
        case WageAndTravelReimbursementRequestStatus.AutoApproved:
            return { label: 'Auto approved', background: '#d1ecf1', color: '#0c5460' };
        case WageAndTravelReimbursementRequestStatus.CenterApproved:
            return { label: 'Center approved', background: '#cce5ff', color: '#004085' };
        case WageAndTravelReimbursementRequestStatus.AdminApproved:
            return { label: 'Admin approved', background: '#e2d4f0', color: '#4a2c6a' };
        case WageAndTravelReimbursementRequestStatus.CenterDeclined:
            return { label: 'Center declined', background: '#f8d7da', color: '#721c24' };
        case WageAndTravelReimbursementRequestStatus.AdminDeclined:
            return { label: 'Admin declined', background: '#f8d7da', color: '#721c24' };
        case WageAndTravelReimbursementRequestStatus.Reimbursed:
            return { label: 'Reimbursed', background: '#d4edda', color: '#155724' };
        default:
            return { label: 'Unknown', background: '#e9ecef', color: '#495057' };
    }
};

const dedupeWeeks = (weeks: IWatrRequestWeeks[]): IWatrRequestWeeks[] => {
    const byWeek = new Map<number, IWatrRequestWeeks>();
    weeks.forEach((week) => {
        if (!byWeek.has(week.weekRequested)) {
            byWeek.set(week.weekRequested, week);
        }
    });
    return Array.from(byWeek.values()).sort((a, b) => a.weekRequested - b.weekRequested);
};

const WeekHistoryTable = ({
    weeks,
    pendingWeeks,
    loading,
    actualWageReimbursementAmount = 0,
    calculatedWeeklyPayByYear = 0,
    employmentType = null,
    incomePercentCovered = 0
}: IProps) => {
    const pendingSet = new Set(pendingWeeks || []);

    if (loading) {
        return (
            <div style={{ marginTop: 16, marginBottom: 4, fontSize: 13, color: '#777' }}>
                Loading week history…
            </div>
        );
    }

    if (!weeks || weeks.length === 0) {
        return (
            <div style={{ marginTop: 16, marginBottom: 4, fontSize: 13, color: '#777' }}>
                No week history available for this request.
            </div>
        );
    }

    const sorted = dedupeWeeks(weeks);
    const rawBaseAmount = isSelfEmployed(employmentType ?? undefined)
        ? (calculatedWeeklyPayByYear || 0)
        : (actualWageReimbursementAmount || 0);
    const roundedBase = Math.round(Math.min(rawBaseAmount, WEEKLY_CAP) * 100) / 100;
    const weeklyWageAmount = calculateAdjustedBaseAmount(roundedBase, incomePercentCovered || 0, WEEKLY_CAP);

    return (
        <div style={{ marginTop: 16, marginBottom: 8 }}>
            <div style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#777', fontWeight: 600, marginBottom: 8 }}>
                Week history
            </div>
            <table className='table table-condensed' style={{ marginBottom: 0, fontSize: 13 }}>
                <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{ width: 110, whiteSpace: 'nowrap' }}>Week</th>
                        <th className='text-right'>Weekly wage amount</th>
                        <th className='text-right'>Other compensation (offset)</th>
                        <th className='text-center'>Request status</th>
                        <th className='text-right'>Amount donor receives</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((week) => {
                        const isPendingBundle = pendingSet.has(week.weekRequested);
                        const otherAmount = week.otherAmount || 0;
                        const amountDonorReceives = calculateAdjustedWeeklyReimbursement(weeklyWageAmount, otherAmount, WEEKLY_CAP);
                        const statusDisplay = getStatusDisplay(week.status);
                        return (
                            <tr
                                key={`${week.requestId}-${week.weekRequested}`}
                                style={isPendingBundle ? { backgroundColor: '#fff8e6' } : undefined}
                            >
                                <td style={{ whiteSpace: 'nowrap' }}>
                                    {week.weekRequested}
                                    {isPendingBundle && (
                                        <span className='text-muted' style={{ marginLeft: 6, fontSize: 11, whiteSpace: 'nowrap' }}>In Review</span>
                                    )}
                                </td>
                                <td className='text-right'>{money(weeklyWageAmount)}</td>
                                <td className='text-right'>{money(otherAmount)}</td>
                                <td className='text-center' style={{ verticalAlign: 'middle' }}>
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            padding: '4px 10px',
                                            borderRadius: 12,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            backgroundColor: statusDisplay.background,
                                            color: statusDisplay.color
                                        }}
                                    >
                                        {statusDisplay.label}
                                    </span>
                                </td>
                                <td className='text-right' style={{ fontWeight: 600 }}>{money(amountDonorReceives)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <div style={{ marginTop: 12, fontSize: 12, color: '#6c757d' }}>
                <div style={{ marginBottom: 4 }}>
                    <strong>Weekly wage amount</strong> is the lost-wage reimbursement for one week
                    (capped at $2,000
                    {incomePercentCovered > 0 && `, reduced by ${incomePercentCovered}% covered elsewhere`}
                    ).
                </div>
                <div style={{ marginBottom: 4 }}>
                    <strong>Other compensation (offset)</strong> is wage reimbursement from another source for that week (not vacation pay). It is deducted from what Donor Shield pays.
                </div>
                <div>
                    <strong>Amount donor receives</strong> = Weekly wage amount − Other compensation (offset), capped at $2,000.
                </div>
            </div>
        </div>
    );
};

export default WeekHistoryTable;
